import * as React from 'react';
import {
  useForm,
  Controller,
  SubmitHandler,
  FormProvider,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  TextField,
  LoadingButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Typography,
} from '@one-thd/sui-atomic-components';
import { WarningFilled } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { getTextFieldStatus } from '../../../utils/form';
import { CardType } from '../../../constants/payments';
import {
  checkingAccountValidationSchema,
  giftCardValidationSchema,
  keytagValidationSchema,
} from './validation';
import { IModalBasicProps } from '../../../models/common/modal';
import { TB2BTenderTypeRequest } from '../../../models/b2b/payments';
import { TAddCardFormValues } from '../../../models/common/payments';
import { getHyphenatedGiftCard } from '../../../utils/general';

type TAcceptedCardTypes =
  | CardType.GIFT_CARD
  | CardType.CHECKING_ACCOUNT
  | CardType.KEY_TAG;

export interface IAddTenderProps {
  cardType: TAcceptedCardTypes;
  onAddCard: (
    cardNickname: string | undefined,
    cardNumber: string,
    routingNumber: string,
  ) => void;
  isLoading: boolean;
}

export const AddCardModal = ({
  cardType,
  hideModal,
  onAddCard,
  isLoading,
}: IAddTenderProps & IModalBasicProps) => {
  const headerMap: Record<TAcceptedCardTypes, () => React.ReactNode> = {
    [CardType.GIFT_CARD]: () => {
      return 'Add Gift Card';
    },
    [CardType.CHECKING_ACCOUNT]: () => {
      return 'Add Checking Account';
    },
    [CardType.KEY_TAG]: () => {
      return 'Add Keytag';
    },
  };

  const nameLabelMap: Record<TAcceptedCardTypes, () => React.ReactNode> = {
    [CardType.GIFT_CARD]: () => {
      return 'Gift Card Nickname';
    },
    [CardType.CHECKING_ACCOUNT]: () => {
      return 'Account Nickname';
    },
    [CardType.KEY_TAG]: () => {
      return 'Keytag Nickname';
    },
  };

  const numberLabelMap: Record<TAcceptedCardTypes, () => React.ReactNode> = {
    [CardType.GIFT_CARD]: () => {
      return 'Gift Card Number*';
    },
    [CardType.CHECKING_ACCOUNT]: () => {
      return 'Account Number*';
    },
    [CardType.KEY_TAG]: () => {
      return 'Keytag Number*';
    },
  };

  const buttonMap: Record<TAcceptedCardTypes, () => React.ReactNode> = {
    [CardType.GIFT_CARD]: () => {
      return 'Add Gift Card';
    },
    [CardType.CHECKING_ACCOUNT]: () => {
      return 'Add Checking Account';
    },
    [CardType.KEY_TAG]: () => {
      return 'Add Keytag';
    },
  };

  const validationMap = {
    [CardType.GIFT_CARD]: () => {
      return giftCardValidationSchema;
    },
    [CardType.CHECKING_ACCOUNT]: () => {
      return checkingAccountValidationSchema;
    },
    [CardType.KEY_TAG]: () => {
      return keytagValidationSchema;
    },
  };

  const childrenMap: Record<TAcceptedCardTypes, () => React.ReactNode> = {
    [CardType.GIFT_CARD]: () => {
      return (
        <>
          <div>
            <Typography variant="body-base">
              You can view all purchases made with a saved gift card in your
              account and receive any associated rebates.
            </Typography>
          </div>
        </>
      );
    },
    [CardType.CHECKING_ACCOUNT]: () => null,
    [CardType.KEY_TAG]: () => null,
  };

  const formMethods = useForm<TB2BTenderTypeRequest>({
    resolver: yupResolver(validationMap[cardType]()),
    mode: 'onTouched',
  });

  const { channel } = React.useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const {
    control,
    formState: { errors: formErrors },
    handleSubmit,
  } = formMethods;

  const onSubmitForm: SubmitHandler<TAddCardFormValues> = ({
    cardNickName,
    cardNumber,
    routingNumber,
  }) => {
    const paymentNumber =
      typeof cardNumber === 'string'
        ? cardNumber.replace(/-/g, '')
        : cardNumber;
    onAddCard(cardNickName, paymentNumber || '', routingNumber || '');
  };

  const headerText = headerMap[cardType]();
  const nameLabelText = nameLabelMap[cardType]();
  const numberLabelText = numberLabelMap[cardType]();
  const buttonText = buttonMap[cardType]();
  const children = childrenMap[cardType]();
  const iconSize = isMobile ? 'small' : 'regular';

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <ModalHeader onClose={hideModal}>{headerText}</ModalHeader>
          <ModalBody>
            {children}
            <div className="sui-gap-4 sui-py-4 sui-w-full sm:sui-w-auto">
              <Controller
                name="cardNickName"
                control={control}
                render={({ field }) => {
                  const { ref, value, name, onChange, ...rest } = field;
                  return (
                    <TextField
                      id="tenderNickNameField"
                      optional
                      fullWidth
                      label={nameLabelText}
                      status={getTextFieldStatus(formErrors.cardNickName)}
                      statusMessage={formErrors.cardNickName?.message}
                      value={value || ''}
                      name={name}
                      onChange={onChange}
                      InputProps={{
                        inputProps: { ...rest, 'data-qm-mask': '' },
                      }}
                    />
                  );
                }}
              />
            </div>
            <div className="sui-gap-4 sui-pb-4 sui-w-full sm:sui-w-auto">
              <Controller
                name="cardNumber"
                control={control}
                render={({ field }) => {
                  const { ref, value, name, onChange, ...rest } = field;

                  const onChangeRevised = (
                    event: React.ChangeEvent<HTMLInputElement>,
                  ) => {
                    if (cardType === CardType.GIFT_CARD) {
                      onChange(
                        getHyphenatedGiftCard(event.currentTarget.value || ''),
                      );
                    } else {
                      onChange(event);
                    }
                  };

                  return (
                    <TextField
                      id="tenderNumberField"
                      fullWidth
                      label={numberLabelText}
                      status={getTextFieldStatus(formErrors.cardNumber)}
                      statusMessage={formErrors.cardNumber?.message}
                      value={value || ''}
                      name={name}
                      onChange={onChangeRevised}
                      InputProps={{
                        inputProps: {
                          ...rest,
                          'data-qm-mask': '',
                        },
                      }}
                    />
                  );
                }}
              />
            </div>
            {cardType === CardType.GIFT_CARD && (
              <div className="sui-flex sui-flex-row">
                <div className="sui-mr-2 sui-mt-1">
                  <WarningFilled color="warning" size={iconSize} />
                </div>
                <div>
                  <Typography variant="body-base" weight="bold">
                    You can’t select saved gift cards in checkout.
                  </Typography>
                  <div className="sui-mt-2">
                    <Typography variant="body-base">
                      Keep your gift card details to enter when checking out
                      online.
                    </Typography>
                  </div>
                </div>
              </div>
            )}
            {cardType === CardType.CHECKING_ACCOUNT && (
              <Controller
                name="routingNumber"
                control={control}
                render={({ field }) => {
                  const { ref, value, onChange, name, ...rest } = field;
                  return (
                    <TextField
                      id="accRoutingNumberField"
                      fullWidth
                      label="Account Routing Number*"
                      status={getTextFieldStatus(formErrors.routingNumber)}
                      statusMessage={formErrors.routingNumber?.message}
                      value={value || ''}
                      onChange={onChange}
                      name={name}
                      InputProps={{
                        inputProps: { ...rest, 'data-qm-mask': '' },
                      }}
                    />
                  );
                }}
              />
            )}
          </ModalBody>
          <ModalFooter position="end">
            <div className="sui-flex sui-gap-4 sm:sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row sm:sui-w-auto">
              <Button variant="secondary" onClick={hideModal}>
                Cancel
              </Button>
              <LoadingButton
                data-testid="addTender"
                variant="primary"
                loading={isLoading}
                type="submit"
              >
                {buttonText}
              </LoadingButton>
            </div>
          </ModalFooter>
        </form>
      </FormProvider>
    </>
  );
};
